export interface Step {
  number: string
  title: string
  body: string
  icon: 'plug' | 'phone' | 'calendar'
}

export interface FeatureTile {
  title: string
  body: string
  icon: 'map' | 'clock' | 'alert' | 'users' | 'shield' | 'inbox' | 'signature'
}

export interface CostCard {
  source: string
  body: string
}

export interface LeakCard {
  title: string
  body: string
  icon: 'missed' | 'database' | 'chaos'
}

export interface FaqItem {
  question: string
  answer: string
}

export const costCards: CostCard[] = [
  {
    source: 'Zillow Premier Agent',
    body: '$500 to $2,000+ per lead in competitive zips. Luxury markets clear $3,000. Only a slice of market share is even available.',
  },
  {
    source: 'Realtor.com',
    body: '$200 to $1,800 per month per zip for Connections Plus, or 30 to 40% of your commission at closing on the referral model.',
  },
  {
    source: 'Homes.com',
    body: '$3,400 a year average membership. CoStar raised new-member pricing again in 2026.',
  },
]

export const leakCards: LeakCard[] = [
  {
    title: 'Missed calls',
    icon: 'missed',
    body: '917 minutes. That is the average agent’s response time to a new inquiry, per lead response research. The buyer signed with someone else 900 minutes ago.',
  },
  {
    title: 'Stale database',
    icon: 'database',
    body: '48% of agents never follow up after the first attempt, while 80% of closings take five or more touches. Your CRM is a graveyard of leads you already paid for.',
  },
  {
    title: 'CRM chaos',
    icon: 'chaos',
    body: 'Leads scattered across portal apps, inboxes, and a CRM nobody updates. No routing, no logging, no owner. Chaos loses the speed race every time.',
  },
]

export const steps: Step[] = [
  {
    number: '01',
    title: 'Connect',
    icon: 'plug',
    body: 'We plug into your existing lines and lead sources. Zillow, Realtor.com, sign calls, your website. Your CRM stays. Your agents change nothing.',
  },
  {
    number: '02',
    title: 'Answer and qualify',
    icon: 'phone',
    body: 'Missed calls get answered live by AI voice, day or night. Forms, texts, and DMs get a response in under 60 seconds. Buyer or seller, price range, timeline, pre-approval, territory. Qualified like your best ISA on their best day.',
  },
  {
    number: '03',
    title: 'Book and log',
    icon: 'calendar',
    body: 'Appointments land straight on the right agent’s calendar by territory and availability. Full transcript and qualification notes written into your CRM the same day.',
  },
]

export const dispatchFeatures: FeatureTile[] = [
  {
    title: 'Territory routing',
    icon: 'map',
    body: 'Zips and neighborhoods map to the right agent or pod.',
  },
  {
    title: 'Time-of-day rules',
    icon: 'clock',
    body: 'Business hours we attempt a warm transfer. After hours we book directly.',
  },
  {
    title: 'Hot seller escalation',
    icon: 'alert',
    body: 'A listing lead with a 90-day timeline can wake the team lead if you want it to.',
  },
  {
    title: 'Weighted round robin',
    icon: 'users',
    body: 'Your top producer can take more. New agents ramp.',
  },
  {
    title: 'Show-rate defense',
    icon: 'shield',
    body: 'Instant confirm, 24 hour and 2 hour reminders with reschedule links.',
  },
  {
    title: 'Every lead source',
    icon: 'inbox',
    body: 'Portal leads, sign calls, forms, and past client calls recognized and routed differently.',
  },
  {
    title: 'Tour agreements, handled',
    icon: 'signature',
    body: 'Booked tour triggers your brokerage’s touring agreement for e-signature, with reminders until it is signed before the showing.',
  },
]

export const promiseItems: { title: string; body: string }[] = [
  {
    title: 'Calls are answered live',
    body: 'Our AI voice agent picks up the ring itself. There is no callback queue and no voicemail step. The caller is talking to a qualified responder on the first ring cycle.',
  },
  {
    title: 'Written leads answer inside 60 seconds',
    body: 'We only use instant capture paths. Webhooks, native forms, and direct lines. Nothing waits on an inbox sync or a batch import, so the clock starts and stops within the minute.',
  },
  {
    title: 'Latency is measured, not assumed',
    body: 'During onboarding we measure capture-to-first-response latency for every connected source, then show you the numbers per source before go-live.',
  },
]

export const stackItems: string[] = [
  'Follow Up Boss',
  'kvCORE',
  'Sierra Interactive',
  'BoomTown',
  'Real Geeks',
  'Lofty',
  'CINC',
]

export interface ProjectOffer {
  name: string
  price: string
  body: string
}

export const projectOffers: ProjectOffer[] = [
  {
    name: 'Response Audit',
    price: 'Free',
    body: 'Bring your CRM to a 15-minute call. With your permission we pull the numbers live: one-touch leads still assigned, contacts with zero activity in 90 days, and your real response times. Numbers only, no pitch.',
  },
  {
    name: 'AI ISA System',
    price: '$2,500 activation + $1,997 per month',
    body: 'Missed calls answered live 24/7, written leads worked in under 60 seconds, booked onto the assigned agent’s calendar, every conversation written back to your CRM.',
  },
  {
    name: 'Database Reactivation Build',
    price: 'from $3,500 + $1,497 per month',
    body: 'Your dormant database, segmented, re-engaged conversationally, and booked. The cheapest deal source you have is the one you already paid for.',
  },
  {
    name: 'CRM Rebuild and Accountability',
    price: 'from $5,000 + $997 per month',
    body: 'Touch standards your CRM enforces, with auto-reassignment the moment a lead misses one. A dashboard that means nobody can let a lead die quietly.',
  },
]

export const builtFor: string[] = [
  'Teams of 3 to 15 agents doing 50+ sides a year',
  'Broker owners running multiple lead sources',
  'Teams paying for Premier Agent, Flex, or Realtor.com leads',
  'Offices with an ISA who needs backup nights and weekends',
]

export const notFor: string[] = [
  'Teams doing under 50 sides a year',
  'Teams that do not buy or generate leads',
  'Anyone looking for a magic lead source',
]

export const howWeWorkItems: string[] = [
  'Flat fees, no per-transaction compensation',
  'No per-minute meters',
  'No per-lead pricing',
  'No long-term lock-in',
  'A guarantee in writing',
  'We never replace your CRM',
]

export const faqItems: FaqItem[] = [
  {
    question: 'Do I have to leave Follow Up Boss or my CRM?',
    answer: 'No. We sit in front of it and write everything back into it.',
  },
  {
    question: 'Will leads know it is AI?',
    answer:
      'You will hear it on the demo before you sign. On your script, your tone. If you can tell it is not your best ISA, do not sign.',
  },
  {
    question: 'What about my existing ISA?',
    answer:
      'We back them up, not replace them. Every lead they miss at lunch, at night, on weekends, we catch.',
  },
  {
    question: 'Can it send our buyer touring agreement?',
    answer:
      'Yes. The moment a tour is booked, the system sends your brokerage’s own touring agreement for e-signature and reminds until it is signed, before the showing. Your forms, your broker’s language. We automate the delivery and the chase, not the legal content.',
  },
  {
    question: 'How fast is setup?',
    answer: 'Under 7 business days once we have access to your lines and calendars.',
  },
  {
    question: 'What does it cost?',
    answer:
      'Projects run from $3,500 and retainers from $997 to $1,997 a month depending on scope. Flat fees, no per-minute billing, no per-lead pricing. The exact number comes out of your audit, not a rate card.',
  },
  {
    question: 'Is this compliant?',
    answer:
      'We answer inbound and respond to leads you generated. No cold texting, full opt-out handling, call recording per your state’s rules.',
  },
]
